
import { Component, Mixins, Prop, Emit, } from "vue-property-decorator";
import { DomainRegistry } from "../../../../../domain";
import { ReceptionDispInfo } from "../../../../../domain/workflow";
import { ReceptionComment, ReceptionCommentSet, } from "../../../../../domain/workflow/reception";
import { ReceptionCommentDispInfo } from "../../../../../domain/workflow/reference";
import { ReceptionCommentKinds, ReceptionCommentUnitKinds, ReceptionCommentSortItemKinds, } from "../../../../../domain/workflow/kinds";
import { ICommentDialogCondition } from "../../../dialogs/ICommentDialogCondition";

@Component
export default class CommentListCommonMixin extends Mixins() {
  @Prop()
  detailId: number;

  @Prop()
  labelSetting: ReceptionDispInfo;

  @Prop()
  commentSetting: ReceptionCommentDispInfo;

  @Emit()
  commentUpdated(): void {}

  get conflictCommentTitle(): string {
    return "競合コメント";
  }

  get cancelButtonLabel(): string {
    return "中止する";
  }

  get commentDefaultSortType(): ReceptionCommentSortItemKinds {
    return (this.commentSetting && this.commentSetting.commentDefaultSortType !== undefined)
      ? this.commentSetting.commentDefaultSortType
      : ReceptionCommentSortItemKinds.UpdateDateTime;
  }

  // 管理者用タブ表示が有効か
  get isAdminCommentTabEnable(): boolean {
    return this.commentSetting
        && this.commentSetting.isAdminCommentTabEnable
        && (this.isRestrictCommentEnable || this.isContactedCommentEnable || this.isDelCommentEnable);
  }

  // コメント管理単位
  get commentUnitKind(): ReceptionCommentUnitKinds {
    return this.commentSetting ? this.commentSetting.commentMode : ReceptionCommentUnitKinds.Multiple;
  }

  // 伝達事項の管理単位
  get nextMessageUnitKind(): ReceptionCommentUnitKinds {
    return this.commentSetting ? this.commentSetting.nextMessageMode : ReceptionCommentUnitKinds.Single;
  }

  // 閲覧制限コメントの管理単位
  get restrictCommentUnitKind(): ReceptionCommentUnitKinds {
    return this.commentSetting ? this.commentSetting.restrictCommentMode : ReceptionCommentUnitKinds.Multiple;
  }

  // 連絡済みコメントの管理単位
  get contactedCommentUnitKind(): ReceptionCommentUnitKinds {
    return this.commentSetting ? this.commentSetting.contactedCommentMode : ReceptionCommentUnitKinds.Multiple;
  }

  // 削除コメントの管理単位
  get deleteCommentUnitKind(): ReceptionCommentUnitKinds {
    return this.commentSetting ? this.commentSetting.delCommentMode : ReceptionCommentUnitKinds.Multiple;
  }

  // 閲覧制限コメント権限があるか
  get isRestrictCommentEnable(): boolean {
    return this.commentSetting && this.commentSetting.isRestrictCommentEnable;
  }

  // 連絡済情報運用権限があるか
  get isContactedCommentEnable(): boolean {
    return this.commentSetting && this.commentSetting.isContactedCommentEnable;
  }

  // 削除コメント運用が有効か
  get isDelCommentEnable(): boolean {
    return this.commentSetting && this.commentSetting.isDelCommentEnable;
  }

  get shouldSortCommentAsc(): boolean {
    return this.commentSetting && this.commentSetting.shouldSortCommentAsc;
  }

  getCommentLabel(kind: ReceptionCommentKinds): string {

    const dispSetting = this.labelSetting ? this.labelSetting : new ReceptionDispInfo(); // ヌルポ対策

    switch (kind) {
      case ReceptionCommentKinds.Comment:
        return dispSetting.commentLabelName;
      case ReceptionCommentKinds.Restrict:
        return dispSetting.restrictCommentLabelName;
      case ReceptionCommentKinds.NextMessage:
        return dispSetting.nextMessageLabelName;
      case ReceptionCommentKinds.BackMessage:
        return dispSetting.backMessageLabelName;
      case ReceptionCommentKinds.ContactedInfo:
        return dispSetting.contactedCommentLabelName;
      case ReceptionCommentKinds.Delete:
        return dispSetting.delCommentLabelName;
      case ReceptionCommentKinds.Conflict:
        return this.conflictCommentTitle;
    }

    return "コメント"; // ここには到達しないようにしてね
  }

  async execDeleteComment(kind: ReceptionCommentKinds, comment: ReceptionComment): Promise<void> {
    const commentLabel = this.getCommentLabel(kind);
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      const result = await repository.deleteComment(comment.id);
      if (result.success) {
        this.$success("成功", commentLabel + "の削除に成功しました。");
        // 親に投げる
        this.commentUpdated();
      } else {
        this.$error("エラー", commentLabel + "の削除に失敗しました。", result.reason);
      }
    } catch (e) {
      this.$error("エラー", commentLabel + "の削除に失敗しました。", e);
    }

  }

  async registerComment(kind: ReceptionCommentKinds, comment: ReceptionComment): Promise<boolean> {
    const commentLabel = this.getCommentLabel(kind);
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      const result = await repository.registerComment(this.detailId, kind, comment);
      if (result.success) {
        this.$success("成功", commentLabel + "の登録に成功しました。");
      } else {
        if (result.isCommentRegistrationFailure) {
          const latestComment = await this.getCommentByCommentId(result.commentId);
          const reeditResult = await this.reeditAndRegisterComment(kind, comment, latestComment);
          if (reeditResult) { return true; }
        }
        this.$error("エラー", commentLabel + "の登録に失敗しました。", result.reason);
        return false;
      }
      // 親に投げる
      this.commentUpdated();
      return true;
    } catch (e) {
      this.$error("エラー", commentLabel + "の登録に失敗しました。", e);
      return false;
    }
  }

  async getCommentByCommentId(commentId: number): Promise<ReceptionComment> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      const result = await repository.getComment(commentId);
      return result;
    } catch (e) {
      this.$error("エラー", "コメントの取得に失敗しました。", e);
    }
  }

  async reeditAndRegisterComment(kind: ReceptionCommentKinds, editingComment: ReceptionComment, latestComment: ReceptionComment): Promise<boolean> {
    const editResult = await this.reeditComment(kind, editingComment.content, latestComment);
    if (editResult === undefined) {
      this.commentUpdated(); // コメント競合して登録中止したので最新のコメントセットを取得し直す
      return false;
    }

    const registerableReceptionComment = latestComment.clone();
    registerableReceptionComment.content = editResult;
    registerableReceptionComment.id = latestComment.deleted ? null : latestComment.id;
    registerableReceptionComment.archiveId = latestComment.deleted ? null : latestComment.archiveId;
    registerableReceptionComment.deleted = false;
    return this.registerComment(kind, registerableReceptionComment);
  }

  async reeditComment(kind: ReceptionCommentKinds, editingComment: string, latestComment: ReceptionComment): Promise<string> {
    return this.$commonDialogs.workflow.conflictCommentComparer.show(
      this.getCommentLabel(kind) + "再編集",
      this.getCommentLabel(kind),
      editingComment,
      latestComment
    );
  }

  toCommentDialogCondition(name: string, note: string, commentSet: ReceptionCommentSet): ICommentDialogCondition {
    return {
      name: name,
      note: note,
      shouldEmphasizeInput: false,
      reference: commentSet
        ? {
          labelSetting: this.labelSetting,
          comments: {
            defaultSortType: this.commentDefaultSortType,
            shouldSortCommentAsc: this.shouldSortCommentAsc,
            nextMessages: {
              unit: this.nextMessageUnitKind,
              comments: commentSet.nextMessages,
            },
            backMessages: {
              unit: this.commentUnitKind,
              comments: commentSet.backMessages,
            },
          }
        }
        : null,
    };
  }
}
